import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api"; // your existing apiFetch
import {BASE_URL as u} from "../../commonUrls";
import { useToast } from "../ToastContext";
import "./FileVault.css";

const BASE_URL = `${u}`;

export default function FileVault(){
  const nav = useNavigate();
  const addToast = useToast();
  const shareInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [sharedFiles, setSharedFiles] = useState([]);
  const [viewMode, setViewMode] = useState("mine");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState("all");
  const [uploadFolder, setUploadFolder] = useState("unsorted");
  const [isUploading, setIsUploading] = useState(false);
  const [linkFilter, setLinkFilter] = useState("all");
  const [noteSearch, setNoteSearch] = useState("");
  const [expandedFiles, setExpandedFiles] = useState(() => new Set());
  const [hoveredFileId, setHoveredFileId] = useState(null);
  const [shareLinkPanelFileId, setShareLinkPanelFileId] = useState(null);
  const [shareLinks, setShareLinks] = useState({});
  const [shareLinkLoading, setShareLinkLoading] = useState(false);
  const [shareModal, setShareModal] = useState(null); // { file } or null
  const [shareInput, setShareInput] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  useEffect(()=>{
    loadFolders();
    loadFiles();
    loadSharedFiles();
  },[]);

  const loadFiles = async ()=>{
    const res = await apiFetch(`${BASE_URL}/files?includeLinks=true`);
    const data = await res.json();
    setFiles(data || []);
    return data || [];
  };

  const loadSharedFiles = async () => {
    const res = await apiFetch(`${BASE_URL}/files/shared`);
    if (!res.ok) {
      setSharedFiles([]);
      return [];
    }
    const data = await res.json();
    setSharedFiles(data || []);
    return data || [];
  };

  const loadFolders = async () => {
    try {
      const res = await apiFetch(`${BASE_URL}/files/folders`);
      if (!res.ok) {
        setFolders([]);
        return [];
      }
      const data = await res.json();
      const next = Array.isArray(data) ? data : [];
      setFolders(next);
      return next;
    } catch (_e) {
      setFolders([]);
      return [];
    }
  };

  /* ---------- UPLOAD ---------- */

  const uploadFiles = async ()=>{
    if(selectedFiles.length === 0) return;

    const fd = new FormData();
    selectedFiles.forEach(f => fd.append("files", f));
    if (uploadFolder && uploadFolder !== "unsorted") {
      fd.append("folderId", uploadFolder);
    }

    try {
      setIsUploading(true);
      const res = await apiFetch(`${BASE_URL}/files/upload`, {
        method:"POST",
        body: fd
      });
      if (!res.ok) {
        alert("Upload failed. Please try again.");
        return;
      }

      setSelectedFiles([]);
      await loadFiles();
    } catch (_e) {
      alert("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const createFolder = async (parentId = null) => {
    const name = window.prompt(parentId ? "Subfolder name:" : "Folder name:");
    if (!name || !name.trim()) return;
    const trimmed = name.trim();
    try {
      const res = await apiFetch(`${BASE_URL}/files/folders`, {
        method: "POST",
        body: JSON.stringify({ name: trimmed, parentId })
      });
      if (!res.ok) {
        alert("Unable to create folder right now.");
        return;
      }
      const created = await res.json();
      setFolders((prev) => [...prev, created]);
      setActiveFolder(created.id);
      setUploadFolder(created.id);
    } catch (_e) {
      alert("Unable to create folder right now.");
    }
  };

  const renameFolder = async (folderId) => {
    const folder = folders.find((f) => f.id === folderId);
    if (!folder) return;
    const name = window.prompt("Rename folder:", folder.name);
    if (!name || !name.trim()) return;
    const trimmed = name.trim();
    try {
      const res = await apiFetch(`${BASE_URL}/files/folders/${folderId}`, {
        method: "PUT",
        body: JSON.stringify({ name: trimmed })
      });
      if (!res.ok) {
        alert("Unable to rename folder right now.");
        return;
      }
      const updated = await res.json();
      setFolders((prev) => prev.map((f) => (f.id === folderId ? updated : f)));
    } catch (_e) {
      alert("Unable to rename folder right now.");
    }
  };

  const deleteFolder = async (folderId) => {
    const folder = folders.find((f) => f.id === folderId);
    if (!folder) return;
    if (!window.confirm(`Delete folder "${folder.name}" and its subfolders? Files will move to Unsorted.`)) return;
    try {
      const res = await apiFetch(`${BASE_URL}/files/folders/${folderId}`, {
        method: "DELETE"
      });
      if (!res.ok) {
        alert("Unable to delete folder right now.");
        return;
      }
      const toDelete = new Set(getDescendantIds(folderId, folders));
      toDelete.add(folderId);
      setFolders((prev) => prev.filter((f) => !toDelete.has(f.id)));
      setFiles((prev) => prev.map((f) => (toDelete.has(f.folderId) ? { ...f, folderId: null } : f)));
      if (toDelete.has(activeFolder)) {
        setActiveFolder("unsorted");
        setUploadFolder("unsorted");
      }
    } catch (_e) {
      alert("Unable to delete folder right now.");
    }
  };

  const moveFileToFolder = async (fileId, folderId) => {
    const normalizedFolderId = folderId === "unsorted" ? null : folderId;
    try {
      const res = await apiFetch(`${BASE_URL}/files/${fileId}/folder`, {
        method: "PUT",
        body: JSON.stringify({ folderId: normalizedFolderId })
      });
      if (!res.ok) {
        alert("Unable to move file right now.");
        return;
      }
      setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, folderId: normalizedFolderId } : f)));
    } catch (_e) {
      alert("Unable to move file right now.");
    }
  };

  const renameFile = async (file) => {
    const name = window.prompt("Rename file:", file.displayName || file.fileName);
    if (!name || !name.trim()) return;
    const trimmed = name.trim();
    try {
      const res = await apiFetch(`${BASE_URL}/files/${file.id}/rename`, {
        method: "PUT",
        body: JSON.stringify({ displayName: trimmed }),
      });
      if (!res.ok) {
        alert("Unable to rename file right now.");
        return;
      }
      const updated = await res.json();
      setFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, displayName: updated.displayName } : f)));
    } catch (_e) {
      alert("Unable to rename file right now.");
    }
  };

  const deleteFile = async (id, fileName) => {
    if (!window.confirm(`Delete "${fileName}"?`)) return;

    try {
      const res = await apiFetch(`${BASE_URL}/files/${id}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        alert("Unable to delete file right now.");
        return;
      }

      loadFiles();
    } catch (_e) {
      alert("Unable to delete file right now.");
    }
  };

  const downloadFile = async (file) => {
    try {
      const res = await apiFetch(`${BASE_URL}/files/${file.id}/download`);
      if (!res.ok) {
        alert("Unable to download file right now.");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.fileName || "download";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) {
      alert("Unable to download file right now.");
    }
  };

  const toggleExpanded = (fileId) => {
    setExpandedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const unlinkNoteFromFile = async (fileId, noteId) => {
    try {
      const res = await apiFetch(`${BASE_URL}/files/${fileId}/links/notes/${noteId}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        alert("Unable to unlink note right now.");
        return;
      }
      setFiles((prev) =>
        prev.map((file) => {
          if (file.id !== fileId) return file;
          const nextNotes = Array.isArray(file.linkedNotes)
            ? file.linkedNotes.filter((note) => note.id !== noteId)
            : [];
          return { ...file, linkedNotes: nextNotes };
        })
      );
    } catch (_e) {
      alert("Unable to unlink note right now.");
    }
  };

  const folderTree = useMemo(() => {
    const byParent = {};
    folders.forEach((f) => {
      const key = f.parentId || "root";
      if (!byParent[key]) byParent[key] = [];
      byParent[key].push(f);
    });
    Object.keys(byParent).forEach((k) => {
      byParent[k].sort((a, b) => a.name.localeCompare(b.name));
    });
    return byParent;
  }, [folders]);

  const fileCounts = useMemo(() => {
    const source = viewMode === "shared" ? sharedFiles : files;
    const direct = { all: source.length, unsorted: 0 };
    folders.forEach((f) => {
      direct[f.id] = 0;
    });
    source.forEach((f) => {
      const folderId = f.folderId;
      if (folderId && direct[folderId] !== undefined) {
        direct[folderId] += 1;
      } else {
        direct.unsorted += 1;
      }
    });
    const memo = {};
    const countFolder = (id) => {
      if (memo[id] !== undefined) return memo[id];
      let total = direct[id] || 0;
      const children = folderTree[id] || [];
      children.forEach((child) => {
        total += countFolder(child.id);
      });
      memo[id] = total;
      return total;
    };
    const counts = { ...direct };
    folders.forEach((f) => {
      counts[f.id] = countFolder(f.id);
    });
    return counts;
  }, [files, folders, folderTree]);

  const filteredFiles = useMemo(() => {
    let result = viewMode === "shared" ? sharedFiles : files;
    if (activeFolder === "unsorted") {
      result = result.filter((f) => !f.folderId);
    } else if (activeFolder !== "all") {
      const allowed = new Set([activeFolder, ...getDescendantIds(activeFolder, folders)]);
      result = result.filter((f) => allowed.has(f.folderId));
    }

    if (linkFilter === "linked") {
      result = result.filter((f) => Array.isArray(f.linkedNotes) && f.linkedNotes.length > 0);
    } else if (linkFilter === "unlinked") {
      result = result.filter((f) => !Array.isArray(f.linkedNotes) || f.linkedNotes.length === 0);
    }

    const search = noteSearch.trim().toLowerCase();
    if (search) {
      result = result.filter((f) =>
        Array.isArray(f.linkedNotes) &&
        f.linkedNotes.some((note) => (note.title || "").toLowerCase().includes(search))
      );
    }

    return [...result].sort((a, b) => {
      const aNote = maxLinkedUpdatedAt(a);
      const bNote = maxLinkedUpdatedAt(b);
      if (aNote !== bNote) return bNote - aNote;
      const aUploaded = toTimestamp(a.uploadedAt);
      const bUploaded = toTimestamp(b.uploadedAt);
      return bUploaded - aUploaded;
    });
  }, [files, sharedFiles, viewMode, activeFolder, folders, linkFilter, noteSearch]);

  useEffect(() => {
    if (viewMode === "shared") {
      setActiveFolder("all");
      setUploadFolder("unsorted");
      return;
    }
    if (activeFolder === "all") {
      setUploadFolder("unsorted");
      return;
    }
    setUploadFolder(activeFolder);
  }, [activeFolder, viewMode]);

  const openShareModal = (file) => {
    setShareModal({ file });
    setShareInput("");
  };

  const closeShareModal = () => {
    setShareModal(null);
    setShareInput("");
  };

  const validateShareEmail = () => {
    const email = shareInput.trim();
    if (!email || !email.includes("@")) {
      addToast("Enter a valid email address.", "error");
      return null;
    }
    return email;
  };

  const submitShareAccess = async () => {
    const email = validateShareEmail();
    if (!email || !shareModal) return;
    setShareLoading(true);
    try {
      const res = await apiFetch(`${BASE_URL}/files/${shareModal.file.id}/share`, {
        method: "POST",
        body: JSON.stringify({ emails: [email] }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        addToast(body.message || "Share failed.", "error");
        return;
      }
      addToast("File shared — recipient notified by email.", "success");
      closeShareModal();
    } catch {
      addToast("Share failed. Check your connection and try again.", "error");
    } finally {
      setShareLoading(false);
    }
  };

  const submitSendFileEmail = async () => {
    const email = validateShareEmail();
    if (!email || !shareModal) return;
    setEmailSending(true);
    try {
      const res = await apiFetch(`${BASE_URL}/files/${shareModal.file.id}/email-attachment`, {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        addToast(body.message || "Failed to send file.", "error");
        return;
      }
      addToast("File sent to " + email + " as email attachment.", "success");
      closeShareModal();
    } catch {
      addToast("Send failed. Check your connection and try again.", "error");
    } finally {
      setEmailSending(false);
    }
  };

  const loadShareLinks = async (fileId) => {
    try {
      const res = await apiFetch(`${BASE_URL}/files/${fileId}/share-links`);
      if (!res.ok) return;
      const data = await res.json();
      setShareLinks((prev) => ({ ...prev, [fileId]: data || [] }));
    } catch (_e) {
      // ignore
    }
  };

  const toggleShareLinkPanel = (fileId) => {
    setShareLinkPanelFileId((prev) => {
      const next = prev === fileId ? null : fileId;
      if (next) loadShareLinks(fileId);
      return next;
    });
  };

  const createShareLink = async (fileId, { expiresAt, maxUses }) => {
    setShareLinkLoading(true);
    try {
      const res = await apiFetch(`${BASE_URL}/files/${fileId}/share-links`, {
        method: "POST",
        body: JSON.stringify({ expiresAt: expiresAt || null, maxUses: maxUses || null }),
      });
      if (!res.ok) {
        alert("Unable to create share link right now.");
        return;
      }
      const created = await res.json();
      setShareLinks((prev) => ({ ...prev, [fileId]: [created, ...(prev[fileId] || [])] }));
    } catch (_e) {
      alert("Unable to create share link right now.");
    } finally {
      setShareLinkLoading(false);
    }
  };

  const revokeShareLink = async (fileId, linkId) => {
    if (!window.confirm("Revoke this share link? Anyone with the link will lose access.")) return;
    try {
      const res = await apiFetch(`${BASE_URL}/files/${fileId}/share-links/${linkId}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        alert("Unable to revoke share link right now.");
        return;
      }
      setShareLinks((prev) => ({
        ...prev,
        [fileId]: (prev[fileId] || []).filter((l) => l.id !== linkId),
      }));
    } catch (_e) {
      alert("Unable to revoke share link right now.");
    }
  };

  const copyShareLinkUrl = async (token) => {
    const url = `${BASE_URL}/files/share/public/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      alert("Link copied to clipboard.");
    } catch (_e) {
      window.prompt("Copy this link:", url);
    }
  };

  return (
    <div className="file-vault-wrap">

      <div className="file-vault-header">
        <h2>📁 File Vault</h2>
        <div className="file-vault-actions-top">
          <div className="file-vault-toggle">
            <button
              className={`file-vault-toggle-btn ${viewMode === "mine" ? "active" : ""}`}
              onClick={() => setViewMode("mine")}
            >
              My files
            </button>
            <button
              className={`file-vault-toggle-btn ${viewMode === "shared" ? "active" : ""}`}
              onClick={() => setViewMode("shared")}
            >
              Shared with me
            </button>
          </div>
          {viewMode === "mine" && (
            <button className="btn" onClick={createFolder}>+ New Folder</button>
          )}
        </div>
      </div>

      <div className="file-vault-layout">
        <aside className="file-vault-sidebar">
          <div className="file-vault-sidebar-section">
            <button
              className={`file-vault-folder-row ${activeFolder === "all" ? "active" : ""}`}
              onClick={() => setActiveFolder("all")}
            >
              <span>All files</span>
              <span className="file-vault-count">{fileCounts.all}</span>
            </button>
            {viewMode === "mine" && (
              <button
                className={`file-vault-folder-row ${activeFolder === "unsorted" ? "active" : ""}`}
                onClick={() => setActiveFolder("unsorted")}
              >
                <span>Unsorted</span>
                <span className="file-vault-count">{fileCounts.unsorted}</span>
              </button>
            )}
          </div>

          {viewMode === "mine" && (
            <div className="file-vault-sidebar-section">
              <div className="file-vault-sidebar-title">Folders</div>
              {folders.length === 0 && (
                <div className="file-vault-empty muted">No folders yet</div>
              )}
              {(folderTree.root || []).map((folder) => (
                <FolderRow
                  key={folder.id}
                  folder={folder}
                  depth={0}
                  activeFolder={activeFolder}
                  fileCounts={fileCounts}
                  onSelect={setActiveFolder}
                  onCreateSubfolder={(id) => createFolder(id)}
                  onRename={renameFolder}
                  onDelete={deleteFolder}
                  childrenByParent={folderTree}
                />
              ))}
            </div>
          )}
        </aside>

        <section className="file-vault-main">
          {/* Upload */}
          {viewMode === "mine" && (
            <div className="file-vault-upload-box">
              <input 
                type="file" 
                multiple 
                onChange={e=>setSelectedFiles([...e.target.files])}
              />

              <select
                className="file-vault-select"
                value={uploadFolder}
                onChange={(e) => setUploadFolder(e.target.value)}
              >
                <option value="unsorted">Upload to: Unsorted</option>
                {(folderTree.root || []).map((folder) => (
                  <FolderOption key={folder.id} folder={folder} depth={0} childrenByParent={folderTree} />
                ))}
              </select>

              <button className="btn btn-primary" onClick={uploadFiles} disabled={isUploading}>
                {isUploading ? "Uploading..." : "⬆ Upload Files"}
              </button>
            </div>
          )}

          <div className="file-vault-filters">
            <div className="file-vault-filter-group">
              <label>Links</label>
              <select
                className="file-vault-select"
                value={linkFilter}
                onChange={(e) => setLinkFilter(e.target.value)}
              >
                <option value="all">All files</option>
                <option value="linked">Linked to notes</option>
                <option value="unlinked">Unlinked</option>
              </select>
            </div>
            <div className="file-vault-filter-group">
              <label>Search by note title</label>
              <input
                className="file-vault-search"
                value={noteSearch}
                onChange={(e) => setNoteSearch(e.target.value)}
                placeholder="Type a note title"
              />
            </div>
          </div>

          {/* List */}
          <div className="file-vault-list-wrap">
            {filteredFiles.length === 0 && <p className="file-vault-empty">No files found</p>}

            {filteredFiles.map(f=>(
              <Fragment key={f.id}>
              <div
                className="file-vault-card"
                onMouseEnter={() => setHoveredFileId(f.id)}
                onMouseLeave={() => setHoveredFileId(null)}
              >
                <div>
                  <strong
                    className={viewMode === "mine" ? "file-vault-name-editable" : ""}
                    title={viewMode === "mine" ? "Click to rename" : undefined}
                    onClick={viewMode === "mine" ? () => renameFile(f) : undefined}
                  >
                    📎 {f.displayName || f.fileName}
                  </strong>
                  {f.displayName && f.displayName !== f.fileName && (
                    <div className="file-vault-original-name">original: {f.fileName}</div>
                  )}
                  <div className="file-vault-meta">
                    {(f.fileSize/1024).toFixed(1)} KB · {f.fileType || "file"}
                    {formatUploadedAt(f.uploadedAt) && ` · Uploaded ${formatUploadedAt(f.uploadedAt)}`}
                  </div>
                  {Array.isArray(f.linkedNotes) && f.linkedNotes.length > 0 && (
                    <div className="file-vault-linked">
                      <div className="file-vault-linked-row">
                        <span>Linked to</span>
                        <span className="file-vault-linked-badge">{f.linkedNotes.length}</span>
                      </div>
                      <div className="file-vault-linked-chips">
                        {getVisibleNotes(f, expandedFiles, hoveredFileId).map((note) => (
                          <button
                            key={note.id}
                            type="button"
                            className="file-vault-note-chip"
                            title={buildNoteTooltip(note)}
                            onClick={() => nav("/notes/new", { state: { note: { id: note.id, title: note.title } } })}
                          >
                            <span className="file-vault-note-chip-text">{note.title || "Untitled"}</span>
                            <span
                              className="file-vault-note-chip-unlink"
                              title="Unlink from note"
                              onClick={(e) => {
                                e.stopPropagation();
                                unlinkNoteFromFile(f.id, note.id);
                              }}
                            >
                              ×
                            </span>
                          </button>
                        ))}
                        {getHiddenCount(f, expandedFiles, hoveredFileId) > 0 && (
                          <button
                            type="button"
                            className="file-vault-note-more"
                            onClick={() => toggleExpanded(f.id)}
                          >
                            +{getHiddenCount(f, expandedFiles, hoveredFileId)} more
                          </button>
                        )}
                        {isExpanded(f.id, expandedFiles) && f.linkedNotes.length > 2 && (
                          <button
                            type="button"
                            className="file-vault-note-more"
                            onClick={() => toggleExpanded(f.id)}
                          >
                            Show less
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="file-vault-actions">
                  {viewMode === "mine" && (
                    <select
                      className="file-vault-select"
                      value={f.folderId || "unsorted"}
                      onChange={(e) => moveFileToFolder(f.id, e.target.value)}
                    >
                      <option value="unsorted">Unsorted</option>
                      {(folderTree.root || []).map((folder) => (
                        <FolderOption key={folder.id} folder={folder} depth={0} childrenByParent={folderTree} />
                      ))}
                    </select>
                  )}
                  <button
                    type="button"
                    className="file-vault-download-btn"
                    onClick={() => downloadFile(f)}
                  >
                    ⬇ Download
                  </button>
                  {viewMode === "mine" && (
                    <>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => renameFile(f)}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => openShareModal(f)}
                      >
                        Share
                      </button>
                      <button
                        type="button"
                        className={`btn ${shareLinkPanelFileId === f.id ? "active" : ""}`}
                        onClick={() => toggleShareLinkPanel(f.id)}
                      >
                        🔗 Link
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => deleteFile(f.id, f.displayName || f.fileName)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
              {shareLinkPanelFileId === f.id && (
                <ShareLinkPanel
                  fileId={f.id}
                  links={shareLinks[f.id] || []}
                  loading={shareLinkLoading}
                  onCreate={createShareLink}
                  onRevoke={revokeShareLink}
                  onCopy={copyShareLinkUrl}
                  buildUrl={(token) => `${BASE_URL}/files/share/public/${token}`}
                />
              )}
              </Fragment>
            ))}
          </div>
        </section>
      </div>

      {shareModal && (
        <div className="modal-overlay" onClick={closeShareModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <span>Share "{shareModal.file.displayName || shareModal.file.fileName}"</span>
              <button className="modal-close" onClick={closeShareModal}>×</button>
            </div>
            <div className="modal-body" style={{ padding: "16px 20px 20px" }}>
              <input
                ref={shareInputRef}
                className="form-input"
                style={{ width: "100%", marginBottom: 16, boxSizing: "border-box" }}
                placeholder="Recipient email address"
                type="email"
                value={shareInput}
                onChange={(e) => setShareInput(e.target.value)}
                autoFocus
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                <div style={{ background: "var(--bg-secondary, #141414)", border: "1px solid var(--border-color, #2a2a2a)", borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text-primary)", marginBottom: 4 }}>Share Access</div>
                  <div style={{ fontSize: 12, color: "var(--color-muted)", marginBottom: 10 }}>
                    Recipient gets access to this file in the app. They are notified by email.
                  </div>
                  <button
                    className="btn"
                    style={{ width: "100%" }}
                    onClick={submitShareAccess}
                    disabled={shareLoading || emailSending || !shareInput.trim()}
                  >
                    {shareLoading ? "Sharing…" : "Share Access"}
                  </button>
                </div>
                <div style={{ background: "var(--bg-secondary, #141414)", border: "1px solid var(--border-color, #2a2a2a)", borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text-primary)", marginBottom: 4 }}>Send as Email Attachment</div>
                  <div style={{ fontSize: 12, color: "var(--color-muted)", marginBottom: 10 }}>
                    The file is sent directly as an email attachment. No account required.
                  </div>
                  <button
                    className="btn btn-secondary"
                    style={{ width: "100%" }}
                    onClick={submitSendFileEmail}
                    disabled={shareLoading || emailSending || !shareInput.trim()}
                  >
                    {emailSending ? "Sending…" : "Send File via Email"}
                  </button>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="btn btn-secondary" onClick={closeShareModal} disabled={shareLoading || emailSending}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getDescendantIds(folderId, allFolders) {
  const byParent = {};
  allFolders.forEach((f) => {
    const key = f.parentId || "root";
    if (!byParent[key]) byParent[key] = [];
    byParent[key].push(f);
  });
  const result = [];
  const visit = (id) => {
    const children = byParent[id] || [];
    children.forEach((child) => {
      result.push(child.id);
      visit(child.id);
    });
  };
  visit(folderId);
  return result;
}

function FolderRow({
  folder,
  depth,
  activeFolder,
  fileCounts,
  onSelect,
  onCreateSubfolder,
  onRename,
  onDelete,
  childrenByParent
}) {
  const paddingLeft = 10 + depth * 14;
  const children = childrenByParent[folder.id] || [];
  return (
    <div className={`file-vault-folder-row-wrap ${activeFolder === folder.id ? "active" : ""}`}>
      <button
        className={`file-vault-folder-row ${activeFolder === folder.id ? "active" : ""}`}
        onClick={() => onSelect(folder.id)}
        style={{ paddingLeft }}
      >
        <span>{folder.name}</span>
        <span className="file-vault-count">{fileCounts[folder.id] || 0}</span>
      </button>
      <div className="file-vault-folder-actions" style={{ marginLeft: paddingLeft }}>
        <button className="btn btn-ghost" onClick={() => onCreateSubfolder(folder.id)}>
          New subfolder
        </button>
        <button className="btn btn-ghost" onClick={() => onRename(folder.id)}>
          Rename
        </button>
        <button className="btn btn-ghost danger" onClick={() => onDelete(folder.id)}>
          Delete
        </button>
      </div>
      {children.map((child) => (
        <FolderRow
          key={child.id}
          folder={child}
          depth={depth + 1}
          activeFolder={activeFolder}
          fileCounts={fileCounts}
          onSelect={onSelect}
          onCreateSubfolder={onCreateSubfolder}
          onRename={onRename}
          onDelete={onDelete}
          childrenByParent={childrenByParent}
        />
      ))}
    </div>
  );
}

function FolderOption({ folder, depth, childrenByParent }) {
  const label = `${"—".repeat(depth)} ${folder.name}`.trim();
  const children = childrenByParent[folder.id] || [];
  return (
    <>
      <option value={folder.id}>{label}</option>
      {children.map((child) => (
        <FolderOption key={child.id} folder={child} depth={depth + 1} childrenByParent={childrenByParent} />
      ))}
    </>
  );
}

function toTimestamp(value) {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

function formatUploadedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const SHARE_LINK_EXPIRY_OPTIONS = [
  { value: "never", label: "Never", ms: null },
  { value: "1h", label: "1 hour", ms: 60 * 60 * 1000 },
  { value: "1d", label: "1 day", ms: 24 * 60 * 60 * 1000 },
  { value: "7d", label: "7 days", ms: 7 * 24 * 60 * 60 * 1000 },
  { value: "30d", label: "30 days", ms: 30 * 24 * 60 * 60 * 1000 },
];

function computeExpiresAt(option) {
  const match = SHARE_LINK_EXPIRY_OPTIONS.find((o) => o.value === option);
  if (!match || !match.ms) return null;
  return new Date(Date.now() + match.ms).toISOString();
}

function ShareLinkPanel({ fileId, links, loading, onCreate, onRevoke, onCopy, buildUrl }) {
  const [expiresOption, setExpiresOption] = useState("never");
  const [maxUsesInput, setMaxUsesInput] = useState("");

  const activeLinks = (links || []).filter((l) => !l.revoked);

  const handleCreate = () => {
    const expiresAt = computeExpiresAt(expiresOption);
    const maxUses = maxUsesInput.trim() ? Number(maxUsesInput.trim()) : null;
    onCreate(fileId, { expiresAt, maxUses });
  };

  return (
    <div className="file-vault-share-panel">
      <div className="file-vault-share-panel-row">
        <label>
          Expires
          <select
            className="file-vault-select"
            value={expiresOption}
            onChange={(e) => setExpiresOption(e.target.value)}
          >
            {SHARE_LINK_EXPIRY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label>
          Max uses
          <input
            type="number"
            min="1"
            className="file-vault-search"
            placeholder="Unlimited"
            value={maxUsesInput}
            onChange={(e) => setMaxUsesInput(e.target.value)}
          />
        </label>
        <button className="btn btn-primary" onClick={handleCreate} disabled={loading}>
          + Create link
        </button>
      </div>

      {activeLinks.length === 0 && (
        <p className="file-vault-empty muted">No active share links</p>
      )}

      {activeLinks.map((link) => (
        <div key={link.id} className="file-vault-share-link-row">
          <div className="file-vault-share-link-url">{buildUrl(link.token)}</div>
          <div className="file-vault-share-link-meta">
            {link.expiresAt ? `Expires ${new Date(link.expiresAt).toLocaleString()}` : "Never expires"}
            {" · "}
            {link.maxUses ? `${link.useCount}/${link.maxUses} uses` : `${link.useCount} uses`}
          </div>
          <div className="file-vault-share-link-actions">
            <button type="button" className="btn" onClick={() => onCopy(link.token)}>Copy</button>
            <button type="button" className="btn btn-danger" onClick={() => onRevoke(fileId, link.id)}>Revoke</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function maxLinkedUpdatedAt(file) {
  if (!Array.isArray(file.linkedNotes) || file.linkedNotes.length === 0) return 0;
  return file.linkedNotes.reduce((max, note) => {
    const ts = toTimestamp(note.updatedAt);
    return ts > max ? ts : max;
  }, 0);
}

function buildNoteTooltip(note) {
  const title = note.title || "Untitled";
  if (!note.updatedAt) return title;
  return `${title} • Updated ${note.updatedAt}`;
}

function getVisibleNotes(file, expandedSet, hoveredFileId) {
  if (!Array.isArray(file.linkedNotes)) return [];
  if (expandedSet.has(file.id) || hoveredFileId === file.id) return file.linkedNotes;
  return file.linkedNotes.slice(0, 2);
}

function getHiddenCount(file, expandedSet, hoveredFileId) {
  if (!Array.isArray(file.linkedNotes)) return 0;
  if (expandedSet.has(file.id) || hoveredFileId === file.id) return 0;
  return Math.max(0, file.linkedNotes.length - 2);
}

function isExpanded(fileId, expandedSet) {
  return expandedSet.has(fileId);
}
